import { OverviewMetrics, OverviewPeriod, ResponseOverviewMetrics } from '@trace/types';
import { DateTime, DurationObjectUnits } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { AuthContextTrace } from '../Authenticate/AuthenticateTrace';
import { callCloud } from '../../fetch/fetch';
import getOverviewDefaultState from './getOverviewDefaultState';
import { OVERVIEW_DATE_FORMAT } from '../../constants/Overview';

interface Payload {
  metrics?: OverviewMetrics;
  duration: string;
  start: string;
  step: keyof DurationObjectUnits;
}

interface HookPayload extends Payload {
  isFetched: boolean;
}

export type HookResult = Pick<UseQueryResult, 'isError' | 'isFetching' | 'refetch'> & HookPayload;

const useOverviewData = (period: OverviewPeriod): HookResult => {
  const { token: traceToken } = useContext(AuthContextTrace);
  const fromDate = DateTime.utc().toFormat(OVERVIEW_DATE_FORMAT);
  const [payload, setPayload] = useState<Payload>(getOverviewDefaultState(period));

  const { data, isError, isFetching, isSuccess, refetch } = useQuery<ResponseOverviewMetrics>(
    ['overviewMetrics', { fromDate, period }],
    () =>
      callCloud<ResponseOverviewMetrics>(
        'GET',
        `/trace/api/metrics/overview?period=${period}&from=${fromDate}`,
        traceToken || '',
      ),
  );

  useEffect(() => {
    if (data && !isFetching) {
      setPayload(data);
    } else if (isFetching) {
      setPayload(getOverviewDefaultState(period));
    }
  }, [data, fromDate, isFetching, period]);

  return {
    isError,
    isFetched: isSuccess,
    isFetching,
    refetch,
    ...payload,
  };
};

export default useOverviewData;
